import React, { type FC } from 'react';

import { withImage } from '@lucid/hooks';

import Box from '../../box/box';
import ImageWithPlaceholder from '../../image/image-with-placeholder';
import VisibleVideo, { type VisibleVideoProps } from '../../video/visible-video/visible-video';
import type { Lucid } from '../../../types/types';

export type CardImageProps = Lucid.Directus.Generated.Directus_Files & {
  /** Allows product to be overwritten */
  product?: Lucid.Site.Product;
};

export type CardMediaProps = Pick<VisibleVideoProps, 'willManualOverride'> & {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The custom aspect ratio for the for the Card Image to conform to
   * @default '4:3'
   */
  aspectRatio?: Lucid.Generic.AspectRatio;
  /** Determines if Card Media will span beyond the padding of the main Card container */
  isFullBleed?: boolean;
  image?: CardImageProps;
};

const CardMedia: FC<CardMediaProps> = ({
  image,
  aspectRatio = '4:3',
  isFullBleed = false,
  willManualOverride,
  className,
  ...rest
}) => {
  const { width, height, translations, type, product } = image ?? {};
  const mediaType = type?.split('/')[0];
  const intrinsicAspectRatio = width && height ? `${width}:${height}` : aspectRatio;

  return (
    <Box
      {...rest}
      css={[
        {
          label: 'cardMedia',
        },
        isFullBleed && {
          width: 'calc(100% + 48px)',
          alignSelf: 'center',
          marginBottom: '-10px',
        },
      ]}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
      data-card-element="media"
      data-testid="card-media"
    >
      {mediaType === 'video' ? (
        <VisibleVideo
          hasExternalPauseButton
          shouldHideBoxShadow
          css={theme => ({
            '& video': {
              borderRadius: theme.radii.r16,
            },
          })}
          src={withImage(image, { product }) || ''}
          // if you pass in 0 it uses the default value instead. Thanks javascript :D
          threshold={0.01}
          willManualOverride={willManualOverride}
        />
      ) : (
        <ImageWithPlaceholder
          alt={translations?.[0]?.alt || ''}
          aspectRatio={intrinsicAspectRatio as Lucid.Generic.AspectRatio}
          className="cardImage"
          isBackgroundImage={isFullBleed}
          src={withImage(image, { product })}
        />
      )}
    </Box>
  );
};

export { CardMedia };
export default CardMedia;
